import { Directive, Input, TemplateRef, ViewContainerRef, OnDestroy, inject } from "@angular/core";

const BREAKPOINTS = {
    xs: 320,
    sm: 768,
    md: 1366,
    lg: 1440,
    xl: 1441,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

function aliasToMediaQuery(alias: string): string {
    const directMatch = alias as Breakpoint;
    if (BREAKPOINTS[directMatch]) {
        const px = BREAKPOINTS[directMatch];
        return `(max-width: ${px}px)`;
    }

    const match = alias.match(/^(lt|gt)-(xs|sm|md|lg|xl)$/);
    if (!match) {
        throw new Error(`Invalid xcMediaIf alias: "${alias}"`);
    }

    const [, dir, size] = match;
    const px = BREAKPOINTS[size as Breakpoint];

    return dir === "lt" ? `(max-width: ${px - 0.02}px)` : `(min-width: ${px}px)`;
}

@Directive({
    selector: "[xcMediaIf]",
})
export class MediaIfDirective implements OnDestroy {
    private readonly templateRef = inject(TemplateRef<any>);
    private readonly viewContainer = inject(ViewContainerRef);

    private mediaQueryList?: MediaQueryList;
    private listener?: (event: MediaQueryListEvent) => void;
    private hasView = false;

    @Input()
    set xcMediaIf(alias: string) {
        this.cleanup();

        const query = aliasToMediaQuery(alias);
        this.mediaQueryList = window.matchMedia(query);
        this.listener = (event: MediaQueryListEvent) => {
            this.updateView(event.matches);
        };

        this.mediaQueryList.addEventListener("change", this.listener);
        this.updateView(this.mediaQueryList.matches);
    }

    private updateView(matches: boolean): void {
        if (matches && !this.hasView) {
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
        } else if (!matches && this.hasView) {
            this.viewContainer.clear();
            this.hasView = false;
        }
    }

    private cleanup(): void {
        if (this.mediaQueryList && this.listener) {
            this.mediaQueryList.removeEventListener("change", this.listener);
        }
        this.viewContainer.clear();
        this.hasView = false;
    }

    ngOnDestroy(): void {
        this.cleanup();
    }
}
